import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ListAndDetailsPanel from "components/layout/ListAndDetailsPanel";
import MissionListPanel from "components/Missions/MissionListPanel";
import MissionSubpage from "components/Missions/MissionSubpage";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as ArrowLeftIcon } from "assets/svgs/ArrowLeft.svg";
import { getMissions } from "lib/fleetMissionStorage/FleetMissionStorageClient";
import type { OrgMission } from "lib/orgServer/OrgServerClient";
import { useOrgFleet } from "providers/OrgFleetProvider";
import type { MissionListItem } from "components/Missions/MissionListView";

const toMissionListItem = (mission: OrgMission): MissionListItem => ({
    key: `${mission.sourceArcSerial}:${mission.sourceMissionId}`,
    sourceArcSerial: mission.sourceArcSerial,
    sourceMissionId: mission.sourceMissionId,
    name: mission.name,
    created_at: mission.createdAt,
    action_count: mission.actionCount,
});

const MissionMapAndDetailsPane = ({
    selectedMission,
    onBack,
}: {
    selectedMission: MissionListItem | null;
    onBack: () => void;
}) => (
    <div className="flex h-full min-h-0 w-full flex-col gap-3">
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-r10 bg-black90 text-h3 text-black40">
            {"<map>"}
        </div>
        <div className="flex min-h-0 w-full flex-1 overflow-hidden rounded-r10 bg-black90">
            {selectedMission ? (
                <MissionSubpage
                    missionId={selectedMission.sourceMissionId}
                    sourceArcSerial={selectedMission.sourceArcSerial}
                    readOnly
                    showBackButton={false}
                    onBack={onBack}
                />
            ) : (
                <div className="flex h-full w-full items-center justify-center p-4 text-h5 text-black60">
                    Select a mission to view its actions.
                </div>
            )}
        </div>
    </div>
);

const FleetMissionsPage = () => {
    const [missions, setMissions] = useState<OrgMission[]>([]);
    const [selectedMission, setSelectedMission] = useState<MissionListItem | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const { proxyHostname } = useOrgFleet();
    const navigate = useNavigate();

    useEffect(() => {
        // An organization switch changes the proxy origin; a selection keyed on
        // the previous organization's mission must not survive it.
        setSelectedMission(null);
        setIsError(false);

        // No proxy assigned to this organization: the read API is unreachable,
        // which is the same user-visible state as a failed load.
        if (!proxyHostname) {
            setMissions([]);
            setIsLoading(false);
            setIsError(true);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        getMissions(proxyHostname)
            .then((items) => {
                if (!cancelled) setMissions(items);
            })
            .catch(() => {
                if (!cancelled) setIsError(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [proxyHostname]);

    const missionItems = useMemo(() => missions.map(toMissionListItem), [missions]);

    return (
        <div className="page-with-top-bar relative flex h-full min-h-0 flex-col space-y-3 p-6">
            <FunctionalButton
                aria-label="Back to dashboard"
                icon={<ArrowLeftIcon className="h-4 w-4 fill-white" />}
                className="absolute right-6 top-2 z-10 h-9 w-9 rounded-r5 border border-primary2 bg-primary2 hover:bg-primary1"
                onClick={() => navigate("/fleet/dashboard")}
            />
            <ListAndDetailsPanel
                className="min-h-0 flex-1"
                listClassName="w-[22rem] min-w-[18rem]"
                list={
                    <MissionListPanel
                        missions={missionItems}
                        isLoading={isLoading}
                        isError={isError}
                        readOnly
                        selectedMissionKey={selectedMission?.key}
                        onSelect={setSelectedMission}
                    />
                }
                details={
                    <MissionMapAndDetailsPane
                        selectedMission={selectedMission}
                        onBack={() => setSelectedMission(null)}
                    />
                }
            />
        </div>
    );
};

export default FleetMissionsPage;
